import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/shared";

const emptyForm = { first_name: "", email: "", company: "", role: "", consent: false, website: "" };

export default function Newsletter() {
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const update = (k) => (e) => {
    const value = k === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim()) return setError("L'adresse électronique est obligatoire.");
    if (!form.consent) return setError("Le consentement est obligatoire pour recevoir la lettre.");

    setStatus("loading");
    try {
      const { data } = await api.post("/newsletter/subscribe", form);
      const messages = {
        subscribed: "Merci. Votre inscription à La lettre BENZENATI est bien enregistrée.",
        reactivated: "Votre inscription a été réactivée. Bon retour parmi les lecteurs de La lettre BENZENATI.",
        already_subscribed: "Cette adresse est déjà inscrite à La lettre BENZENATI.",
        ok: "Merci pour votre inscription.",
      };
      setMessage(messages[data.status] || messages.ok);
      setStatus("done");
      setForm(emptyForm);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Une erreur est survenue.");
      setStatus("idle");
    }
  };

  return (
    <>
      <Seo
        title="La lettre BENZENATI — Inscription à la newsletter"
        description="Inscrivez-vous à La lettre BENZENATI : une sélection périodique de décryptages, outils et points de vigilance utiles aux dirigeants de PME. Pas de publicité, pas de revente de données."
        path="/newsletter"
      />

      <section className="border-b border-bz-line" data-testid="newsletter-page">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-28">
          {/* Présentation */}
          <div>
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="h-px w-10 bg-bz-amber" />
                <span className="bz-label text-bz-petrol">La lettre BENZENATI</span>
              </div>
              <h1 className="mt-8 font-serif text-4xl leading-[1.05] tracking-tight text-bz-navy sm:text-5xl">
                Uniquement des sujets qui méritent d'être compris
              </h1>
              <p className="mt-8 text-lg leading-relaxed text-bz-slate">
                Une sélection périodique de décryptages, outils et points de vigilance utiles aux dirigeants de PME. Pas de bruit supplémentaire : uniquement des sujets qui méritent d'être compris.
              </p>
              <p className="mt-6 border-l-2 border-bz-amber pl-6 text-base leading-relaxed text-bz-slate">
                Recevez uniquement les décryptages qui méritent l'attention d'un dirigeant de PME. Pas de publicité, pas de revente de données et une désinscription possible à tout moment.
              </p>
            </Reveal>
          </div>

          {/* Formulaire */}
          <div>
            <Reveal delay={0.1}>
              {status === "done" ? (
                <div className="border border-bz-line bg-white p-10 lg:p-12" data-testid="newsletter-confirmation">
                  <CheckCircle2 size={40} strokeWidth={1.2} className="text-bz-petrol" />
                  <h2 className="mt-6 font-serif text-2xl text-bz-navy">Inscription confirmée</h2>
                  <p className="mt-4 text-base leading-relaxed text-bz-slate">{message}</p>
                  <button
                    onClick={() => setStatus("idle")}
                    data-testid="newsletter-new-again"
                    className="mt-8 text-sm text-bz-petrol underline underline-offset-4"
                  >
                    Inscrire une autre adresse
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="border border-bz-line bg-white p-8 lg:p-10" data-testid="newsletter-form" noValidate>
                  <div className="hidden" aria-hidden="true">
                    <label>
                      Ne pas remplir
                      <input type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={update("website")} data-testid="newsletter-website" />
                    </label>
                  </div>

                  <div className="space-y-6">
                    <Field label="Prénom (facultatif)" testid="newsletter-firstname" value={form.first_name} onChange={update("first_name")} />
                    <Field label="Adresse électronique" required type="email" testid="newsletter-email" value={form.email} onChange={update("email")} />
                    <Field label="Entreprise (facultatif)" testid="newsletter-company" value={form.company} onChange={update("company")} />
                    <Field label="Fonction (facultatif)" testid="newsletter-role" value={form.role} onChange={update("role")} />

                    <label className="flex items-start gap-3 text-sm leading-relaxed text-bz-slate">
                      <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={update("consent")}
                        data-testid="newsletter-consent"
                        className="mt-1 h-4 w-4 shrink-0 accent-bz-navy"
                      />
                      <span>
                        J'accepte de recevoir La lettre BENZENATI et je comprends que mes données ne seront ni revendues, ni utilisées à des fins publicitaires. Je peux me désinscrire à tout moment.
                      </span>
                    </label>

                    {error && (
                      <p className="text-sm text-red-600" data-testid="newsletter-error">{error}</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "loading"}
                      data-testid="newsletter-submit"
                      className="inline-flex w-full items-center justify-center bg-bz-navy px-8 py-4 text-xs uppercase tracking-[0.18em] text-bz-ivory transition-colors duration-300 hover:bg-bz-petrol disabled:opacity-60"
                    >
                      {status === "loading" ? "Enregistrement…" : "S'inscrire à la lettre"}
                    </button>

                    <p className="text-xs leading-relaxed text-bz-slate/70">
                      Vos données sont conservées uniquement pour l'envoi de la lettre. Voir la{" "}
                      <a href="/politique-de-confidentialite" className="underline underline-offset-2">politique de confidentialité</a>.
                    </p>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, testid, value, onChange, type = "text", required = false }) => (
  <label className="block">
    <span className="bz-label text-bz-slate">{label}{required && <span className="text-bz-amber"> *</span>}</span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="mt-3 w-full border border-bz-line bg-bz-ivory px-4 py-3 text-base text-bz-navy outline-none transition-colors focus:border-bz-petrol"
    />
  </label>
);
